import React, { Component, FormEvent, ReactNode, useState } from 'react';
import { Button } from '@components-library/Button';
import { Text } from '@components-library/Typography';
import { DateTimeRange } from '@components-shared/forms/DateTimeRange';
import { DateTimeRangeOptionType } from '@components-shared/forms/DateTimeRange/types';
import { DynamicDropdown } from '@components-shared/forms/DynamicDropdown/DynamicDropdown';
import { Input } from '@components-shared/forms/Input';
import { MultiSelect, IMultiSelectOption } from '@components-shared/forms/MultiSelect';
import { Select } from '@components-shared/forms/Select';
import Checkbox from '@components-shared/forms/checkbox';
import CustomSelect from '@components-shared/forms/customSelect';
import DateTime from '@components-shared/forms/datetimePicker';
import { FieldValue, IFormField } from '@components-shared/forms/formField.interface';
import NumberRange from '@components-shared/forms/numberRange';
import validators from '@components-shared/forms/validators';
import { pushExportEvent } from '@components-shared/googleAnalyticsEvents';
import { calculateScreenSize } from '@components-shared/utils/calculateScreenSize';
import { screenSizeObserver } from '@components-shared/utils/screenSizeObesrver';
import { keyMap } from '@locales/lokalise/keyMap';
import { handleError } from '@utils/errors';
import { getFileUrlForDownloading } from '@utils/fileDownload';
import { IUtilsDateTimeRange } from '@utils/library/dateTime';
import { observer } from 'mobx-react';
import { useMobxStore } from 'store/mobx';
//@ts-ignore: remove after translating into typescript
import { getInitialState } from '../../forms/formGenerator';
import {
  ITableFiltersForm,
  ITableFiltersFields,
  ITableValidationType,
  ITableFieldTypes,
  ITableFilterFieldType,
  ITableFiltersFieldsValues,
  ITableMultiSelectFieldType,
  ITableSelectFieldType,
  ITableCustomSelectFieldType,
  ITableDynamicDropdownFieldType,
  ITableInputFieldType,
  ITableFilter,
  ITableFilterState,
  ITableFilterProps,
} from './interfaces';

class TableFilter extends Component<ITableFilterProps, ITableFilterState> implements ITableFilter {
  state = {
    form: { ...getInitialState(this.props.fields as IFormField[], this.props.parentContext) },
    validation: this.getValidation(this.props.fields),
    screenSize: calculateScreenSize(window.innerWidth),
  };

  onScreenSizeUpdate = (screenSize: ITableFilterState['screenSize']) => {
    this.setState({ screenSize });
  };

  componentDidMount(): void {
    screenSizeObserver.subscribe(this.onScreenSizeUpdate);

    this.props.setGetFilterValuesRef?.(this.getFilter);
  }

  componentWillUnmount(): void {
    screenSizeObserver.unsubscribe(this.onScreenSizeUpdate);
  }

  getValidation(fields: ITableFiltersFields) {
    return fields.reduce((acc: ITableValidationType, f: ITableFieldTypes) => {
      acc[f.name] = { valid: true, message: '' };
      return acc;
    }, {});
  }

  checkField = (form: ITableFiltersForm, f: ITableFieldTypes) => {
    const field = form[f.name];
    const fieldType = f.type;

    if (f.required) {
      if (field === '' || field == null || field == undefined || (Array.isArray(field) && field.length === 0)) {
        return { valid: false, message: keyMap.formsValidationRequiredError };
      }
    }

    if (validators[fieldType]) {
      const validator = validators[fieldType];
      const params = { ...f };

      //@ts-ignore: remove after translating into typescript
      if (!validator.check(field, params)) {
        return { valid: false, message: validator.message };
      }
    }

    if (f.validator) {
      const { validator } = f;

      if (!validator.check(field as FieldValue)) {
        return { valid: false, message: validator.message };
      }
    }

    return { valid: true, message: '' };
  };

  update(_fields: ITableFiltersFields, needUpdate: boolean) {
    const fields = _fields;

    this.setState(
      {
        validation: this.getValidation(fields),
        form: { ...getInitialState(fields as IFormField[]) },
      },
      () => needUpdate && this.props.onFilter(),
    );
  }

  getFieldInfo = (name: string) => {
    return this.props.fields.find((x) => x.name == name);
  };

  validate = () => {
    const { form } = this.state;
    const { fields } = this.props;
    let isValid = true;

    const setValidateField = () =>
      fields.reduce((validation: ITableValidationType, f: ITableFilterFieldType) => {
        validation[f.name] = this.checkField(form, f);

        if (!validation[f.name].valid) {
          isValid = false;
        }

        return validation;
      }, {});

    const validation = setValidateField();

    this.setState({ validation });

    return isValid;
  };

  getValidationField(name: string) {
    const { validation } = this.state;

    return validation[name];
  }

  onChange = (name: string, value: ITableFiltersFieldsValues | Array<IMultiSelectOption>) => {
    const { form } = this.state;

    form[name] = value;
    this.setState({ form });
  };

  onDatetimeChange = ({ name, value }: { name: string; value: string }) => {
    const { form } = this.state;

    form[name] = value;
    this.setState({ form });
  };

  multiselect = (props: ITableMultiSelectFieldType, value: Array<IMultiSelectOption>) => (
    <MultiSelect
      key={props.name}
      {...props}
      onChange={this.onChange}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  checkbox = (props: ITableFilterFieldType, value: boolean) => (
    <Checkbox
      key={props.name}
      {...props}
      onChange={({ target }) => this.onChange(target.name, target.checked)}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  select = (props: ITableSelectFieldType, value: string) => (
    <Select
      key={props.name}
      {...props}
      onChange={this.onChange}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  customSelect = (props: ITableCustomSelectFieldType, value: string) => (
    <CustomSelect
      key={props.name}
      {...props}
      onChange={this.onChange}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
      showChooseText={true}
    />
  );

  dynamicDropDown = (props: ITableDynamicDropdownFieldType, value: string) => (
    <DynamicDropdown
      key={props.name}
      {...props}
      onChange={this.onChange}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  input = (props: ITableInputFieldType, value: string) => (
    <Input
      key={props.name}
      {...props}
      onChange={this.onChange}
      placeholder={props.placeholder}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  dateTimeRange = (props: ITableFilterFieldType) => {
    const defaultValue =
      typeof props.defaultValue === 'function' ? props.defaultValue(this.props.parentContext) : props.defaultValue;

    return (
      <DateTimeRange
        key={props.name}
        {...props}
        onChange={this.onDatetimeChange}
        defaultValue={defaultValue as IUtilsDateTimeRange}
        options={props.options as DateTimeRangeOptionType[]}
        valid={this.getValidationField(props.name).valid}
        validationMessage={this.getValidationField(props.name).message}
      />
    );
  };

  numberRange = (props: ITableFilterFieldType) => (
    <NumberRange
      key={props.name}
      {...props}
      onChange={this.onChange}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  datetime = (props: ITableFilterFieldType, value: string) => (
    <DateTime
      key={props.name}
      {...props}
      onChange={this.onChange}
      value={value}
      valid={this.getValidationField(props.name).valid}
      validationMessage={this.getValidationField(props.name).message}
    />
  );

  getFilter = () => this.state.form;

  onFilter = (e: FormEvent<HTMLFormElement>): boolean | void => {
    e.preventDefault();
    const { onFilter } = this.props;

    if (!this.validate()) {
      return false;
    }

    return onFilter();
  };

  getFilterClass() {
    const { fields, className = '' } = this.props;

    if (!className) {
      let fieldsCount = 0;
      const fieldsInRow = 4;

      fields.forEach((f) => (f.type === 'dateTimeRange' ? (fieldsCount += 3) : (fieldsCount += 1)));
      const rowsCount = Math.ceil(fieldsCount / fieldsInRow);

      const rowsCountAdjustifierMap: Record<ITableFilterState['screenSize'], number> = {
        xs: 1,
        sm: 1,
        md: 1,
        lg: 1,
        xl: 0,
      };

      return fieldsCount > 4 ? `multi-rows rows--${rowsCount + rowsCountAdjustifierMap[this.state.screenSize]}` : '';
    }

    return className;
  }

  render(): ReactNode {
    const { fields, exportAPI, processing } = this.props;
    const { form } = this.state;
    const className = this.getFilterClass();
    const anyNoHiddenFilters = fields.find((field) => field.type !== 'hidden');

    return (
      <form className={`table-filter ${className}`} onSubmit={this.onFilter}>
        <div className="table-filter__fields">
          {fields.map((field) => {
            if (field.type === 'hidden') {
              return null;
            }

            if (!field.type || !this[field.type as keyof TableFilter]) {
              return (
                <div key={field.name} className="table-filter__fieldset">
                  {this.input(field, form[field.name])}
                </div>
              );
            }

            return (
              <div key={field.name} className="table-filter__fieldset">
                {/*  @ts-ignore */}
                {this[field.type as keyof TableFilter](field, form[field.name])}
              </div>
            );
          })}
        </div>

        <div className="table-filter__btns">
          {anyNoHiddenFilters && (
            <Button type="submit" className="form__button" loaderType="mini" showButtonPreloader={processing}>
              <Text>{keyMap.formsFilter}</Text>
            </Button>
          )}
          {exportAPI && (
            <DownloadButton
              getFilter={this.getFilter}
              exportAPI={this.props.exportAPI}
              exportEvent={this.props.exportEvent}
            />
          )}
        </div>
      </form>
    );
  }
}

export default TableFilter;

export const DownloadButton = observer(
  (props: { getFilter: () => unknown; exportAPI?: string; exportEvent?: string }) => {
    const [isExportProcessing, setIsExportProcessing] = useState(false);
    const {
      accountStore: {
        userInfo: { idHash },
      },
    } = useMobxStore();

    const onDownload = async (): Promise<boolean | undefined> => {
      const link = document.createElement('a');

      const filter = props.getFilter();

      try {
        setIsExportProcessing(true);
        const { url, fileName } = await getFileUrlForDownloading(props.exportAPI ?? '', filter);

        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        if (props.exportEvent) {
          pushExportEvent(idHash, props.exportEvent);
        }
      } catch (error) {
        handleError('Export failed', error);
        return false;
      } finally {
        setIsExportProcessing(false);
        if (link?.parentNode) {
          link.parentNode.removeChild(link);
        }
      }
    };

    return (
      <Button onClick={onDownload} className="form__button" loaderType="mini" showButtonPreloader={isExportProcessing}>
        {keyMap.formsExport}
      </Button>
    );
  },
);
